import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {UpdateIssueFieldValueInput} from './__generated__/updateIssueFieldValueMutation.graphql'
import {ISSUE_FIELD_VALUES_PAGE_SIZE} from './constants'
import {getFieldValueType, replaceFieldValueInCollection, updateNodeValues} from './helpers'

export function commitUpdateIssueFieldValueMutation({
  environment,
  input,
  onCompleted,
  onError,
}: {
  environment: Environment
  input: UpdateIssueFieldValueInput
  onCompleted?: () => void
  onError?: (error?: Error) => void
}) {
  return commitMutation(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation updateIssueFieldValueMutation($input: UpdateIssueFieldValueInput!) {
        updateIssueFieldValue(input: $input) {
          issueFieldValue {
            ... on IssueFieldTextValue {
              id
              value
            }
            ... on IssueFieldSingleSelectValue {
              id
              name
              color
              description
            }
            ... on IssueFieldDateValue {
              id
              value
            }
            ... on IssueFieldNumberValue {
              id
              numberValue: value
            }
          }
          issue {
            id
          }
        }
      }
    `,
    variables: {
      input,
    },
    onCompleted: () => {
      onCompleted?.()
    },
    onError: error => onError?.(error),
    optimisticUpdater: store => {
      const issue = store.get(input.issueId)
      const fieldValues = issue?.getLinkedRecord('issueFieldValues', {first: ISSUE_FIELD_VALUES_PAGE_SIZE})
      const nodes = fieldValues?.getLinkedRecords('nodes') || []
      const fieldInput = input.issueField
      const type = getFieldValueType(fieldInput)
      let node = nodes.find(n => n.getLinkedRecord('field')?.getDataID() === fieldInput.fieldId)
      if (!node) {
        node = store.create(`client:updateFieldValue:${fieldInput.fieldId}`, type)
        const fieldRecord = store.get(fieldInput.fieldId)
        if (fieldRecord) node.setLinkedRecord(fieldRecord, 'field')
      }
      updateNodeValues(store, node, fieldInput)

      replaceFieldValueInCollection(fieldValues, fieldInput.fieldId, node)
    },
    updater: store => {
      const issue = store.get(input.issueId)
      const fieldValues = issue?.getLinkedRecord('issueFieldValues', {first: ISSUE_FIELD_VALUES_PAGE_SIZE})
      const payload = store.getRootField('updateIssueFieldValue')
      const newValue = payload?.getLinkedRecord('issueFieldValue')
      const fieldInput = input.issueField

      if (!newValue) {
        return
      }

      // Ensure field link is set on new value
      if (!newValue.getLinkedRecord('field')) {
        const fieldRecord = store.get(fieldInput.fieldId)
        if (fieldRecord) newValue.setLinkedRecord(fieldRecord, 'field')
      }

      replaceFieldValueInCollection(fieldValues, fieldInput.fieldId, newValue)
    },
  })
}
